import { AuthorHero, type AuthorHeroData } from '@/components/journal/author-hero';
import MetaHead from '@/components/meta-head';
import PublicLayout from '@/layouts/public-layout';
import { ApiFetchError, apiFetch } from '@/lib/api-fetch';
import { Link } from '@inertiajs/react';
import { type FormEvent, useState } from 'react';

interface AuthorShowProps {
    author: AuthorHeroData & { slug: string };
    articles: Array<{
        id: number;
        title: string;
        slug: string;
        excerpt: string | null;
        main_image: string | null;
        published_at: string | null;
        category_name: string | null;
    }>;
}

export default function AuthorShow({ author, articles }: AuthorShowProps) {
    const metaTitle = `${author.name} | LuxRally Journal`;
    const metaDescription = author.bio ?? `Articles by ${author.name} on LuxRally Journal.`;

    return (
        <PublicLayout>
            <MetaHead title={metaTitle} description={metaDescription} />
            <div style={{ backgroundColor: '#F5F5F5', minHeight: '100vh' }}>
                <div className="pt-16 md:pt-20">
                    <AuthorHero author={author} nameLinksToProfile={false} linkToExternalProfile withTopBorder={false} />
                </div>

                <SubscribeBand authorName={author.name} authorSlug={author.slug} />

                <div className="mx-auto max-w-5xl px-6 pt-6 pb-16">
                    <div className="mb-10 flex items-center gap-6">
                        <span className="h-px flex-1" style={{ backgroundColor: 'rgba(14, 39, 39, 0.1)' }} />
                        <h2
                            className="text-[11px] font-medium tracking-[0.3em] whitespace-nowrap uppercase"
                            style={{ color: 'rgba(14, 39, 39, 0.45)' }}
                        >
                            {articles.length > 0 ? `All articles by ${author.name}` : 'No articles yet'}
                        </h2>
                        <span className="h-px flex-1" style={{ backgroundColor: 'rgba(14, 39, 39, 0.1)' }} />
                    </div>
                    {articles.length > 0 && (
                        <div className="grid gap-10 md:grid-cols-3">
                            {articles.map((item) => (
                                <Link key={item.id} href={`/journal/${item.slug}`} className="group">
                                    {item.main_image && (
                                        <div className="aspect-[4/3] overflow-hidden">
                                            <img
                                                src={item.main_image}
                                                alt={item.title}
                                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                loading="lazy"
                                            />
                                        </div>
                                    )}
                                    <div className="mt-4">
                                        {item.category_name && (
                                            <p className="mb-2 text-[11px] tracking-[0.25em] uppercase" style={{ color: 'rgba(14, 39, 39, 0.45)' }}>
                                                {item.category_name}
                                            </p>
                                        )}
                                        <h4
                                            className="line-clamp-2 text-lg font-normal transition-colors group-hover:text-[#0b2222]/70"
                                            style={{
                                                fontFamily: "'Didot', 'Playfair Display', Georgia, serif",
                                                color: '#0e2727',
                                            }}
                                        >
                                            {item.title}
                                        </h4>
                                        {item.excerpt && (
                                            <p className="mt-2 line-clamp-2 text-sm" style={{ color: 'rgba(14, 39, 39, 0.6)' }}>
                                                {item.excerpt}
                                            </p>
                                        )}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </PublicLayout>
    );
}

function SubscribeBand({ authorName, authorSlug }: { authorName: string; authorSlug: string }) {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [state, setState] = useState<'idle' | 'submitting' | 'done'>('idle');
    const [error, setError] = useState<string | null>(null);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        if (!email.trim() || state === 'submitting') return;
        setState('submitting');
        setError(null);
        try {
            await apiFetch(`/api/journal/author/${authorSlug}/subscribe`, {
                method: 'POST',
                body: JSON.stringify({ email: email.trim(), name: name.trim() || undefined }),
                silent: true,
            });
            setState('done');
        } catch (err) {
            let message = 'Something went wrong — please try again.';
            if (err instanceof ApiFetchError && err.status === 422) {
                const body = err.body as { message?: string; errors?: Record<string, string[]> } | undefined;
                message = body?.errors?.email?.[0] ?? body?.message ?? 'Please enter a valid email address.';
            }
            setError(message);
            setState('idle');
        }
    };

    return (
        <div className="mx-auto max-w-5xl px-6 pt-10">
            <div className="border px-6 py-10 text-center md:px-12" style={{ borderColor: 'rgba(14, 39, 39, 0.12)', backgroundColor: '#fdfaf3' }}>
                <p className="text-[11px] font-medium tracking-[0.3em] uppercase" style={{ color: 'rgba(14, 39, 39, 0.45)' }}>
                    The Journal, by email
                </p>
                <h3
                    className="mt-3 text-2xl font-normal md:text-3xl"
                    style={{ fontFamily: "'Didot', 'Playfair Display', Georgia, serif", color: '#0e2727' }}
                >
                    Subscribe to {authorName}
                </h3>
                {state === 'done' ? (
                    <p className="mt-5 text-sm" style={{ color: 'rgba(14, 39, 39, 0.7)' }}>
                        Check your inbox to confirm your subscription.
                    </p>
                ) : (
                    <>
                        <p className="mt-2 text-sm" style={{ color: 'rgba(14, 39, 39, 0.6)' }}>
                            New articles, straight to your inbox. No noise, unsubscribe anytime.
                        </p>
                        <form onSubmit={submit} className="mx-auto mt-6 flex max-w-xl flex-col gap-3 sm:flex-row">
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Name (optional)"
                                className="flex-1 border bg-white px-4 py-2 text-sm outline-none focus:border-[#0b2222]"
                                style={{ borderColor: 'rgba(14, 39, 39, 0.2)', color: '#0e2727' }}
                            />
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email address"
                                className="flex-1 border bg-white px-4 py-2 text-sm outline-none focus:border-[#0b2222]"
                                style={{ borderColor: 'rgba(14, 39, 39, 0.2)', color: '#0e2727' }}
                            />
                            <button
                                type="submit"
                                disabled={state === 'submitting'}
                                className="rounded-sm px-4 py-1.5 text-[11px] font-medium tracking-[0.2em] uppercase transition-opacity hover:opacity-85 disabled:opacity-60"
                                style={{ backgroundColor: '#0b2222', color: '#F5F5F5' }}
                            >
                                {state === 'submitting' ? 'Subscribing…' : 'Subscribe'}
                            </button>
                        </form>
                        {error && (
                            <p className="mt-3 text-sm" style={{ color: '#7C4D2E' }}>
                                {error}
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
